import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
// Add page imports here
import Home from '@/pages/Home';
import Dashboard from '@/pages/Dashboard';
import RunSheet from '@/pages/RunSheet';
import ProtectedRoute from '@/components/ProtectedRoute';
import MobileTabBar from '@/components/MobileTabBar';
import { ThemeProvider } from '@/lib/ThemeContext';
import Settings from '@/pages/Settings';
import AdminUserManagement from '@/pages/AdminUserManagement';
import AdminAuditLogsPage from '@/pages/AdminAuditLogsPage';
import AdminRequestsPage from '@/pages/AdminRequestsPage';
import Tools from '@/pages/Tools';
import Order from '@/pages/Order';
import ToolComingSoon from '@/pages/ToolComingSoon';
import Calculators from '@/pages/Calculators';
import DailyLog from '@/pages/DailyLog';
import DailyReport from '@/pages/DailyReport';
import Handover from '@/pages/Handover';
const Reporting = lazy(() => import('@/pages/Reporting'));
const Conventional = lazy(() => import('@/pages/Conventional'));
const ViscosityCheck = lazy(() => import('@/pages/ViscosityCheck'));
const FlowRate = lazy(() => import('@/pages/FlowRate'));
import { AnimatePresence } from 'framer-motion';
import PageTransition from '@/components/PageTransition';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import ClientPortalLogin from '@/pages/ClientPortalLogin';
import Legal from '@/pages/Legal';
import DocumentBuilder from '@/pages/DocumentBuilder';
import MethodRunSheet from '@/pages/MethodRunSheet';
import Documents from '@/pages/Documents';
import FeaturePermissions from '@/pages/FeaturePermissions';
import FeatureRoute from '@/components/FeatureRoute';
import FeaturePermissionContextProvider from '@/lib/FeaturePermissionContext';

const WEB_BASE_PATH = import.meta.env.VITE_APP_BASE_PATH || "/";

const PageFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin dark:border-slate-800 dark:border-t-slate-300" />
  </div>
);

const HandoverRoute = () => {
  const location = useLocation();
  const formId = location.pathname.split("/").pop();
  return <PageTransition><Handover formId={formId} /></PageTransition>;
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin dark:border-slate-800 dark:border-t-slate-300"></div>
      </div>
    );
  }

  // Only show a registration-specific error here. Authentication routing is
  // handled by ProtectedRoute after the local auth state has been hydrated.
  if (authError?.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  // Render the main app
  return (
    <>
    <Suspense fallback={<PageFallback />}>
    <AnimatePresence mode="wait">
    <Routes location={location} key={location.pathname}>
    {/* Add your page Route elements here */}
    <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
      <Route path="/" element={<PageTransition><Home /></PageTransition>} />
      <Route path="/dashboard" element={<FeatureRoute feature="dashboard"><PageTransition><Dashboard /></PageTransition></FeatureRoute>} />
      <Route path="/run-sheet/:sheetId" element={<PageTransition><RunSheet /></PageTransition>} />
      <Route path="/settings" element={<FeatureRoute feature="settings"><PageTransition><Settings /></PageTransition></FeatureRoute>} />
      <Route path="/admin/user-management" element={<FeatureRoute feature="user_management"><PageTransition><AdminUserManagement /></PageTransition></FeatureRoute>} />
      <Route path="/admin/logs" element={<FeatureRoute feature="audit_logs"><PageTransition><AdminAuditLogsPage /></PageTransition></FeatureRoute>} />
      <Route path="/admin/requests" element={<FeatureRoute feature="admin_requests"><PageTransition><AdminRequestsPage /></PageTransition></FeatureRoute>} />
      <Route path="/tools" element={<FeatureRoute feature="tools"><PageTransition><Tools /></PageTransition></FeatureRoute>} />
      <Route path="/order" element={<FeatureRoute feature="order"><PageTransition><Order /></PageTransition></FeatureRoute>} />
      <Route path="/daily-log" element={<FeatureRoute feature="daily_log"><PageTransition><DailyLog /></PageTransition></FeatureRoute>} />
      <Route path="/daily-report" element={<FeatureRoute feature="daily_report"><PageTransition><DailyReport /></PageTransition></FeatureRoute>} />
      <Route path="/tools/viscosity-check" element={<FeatureRoute feature="viscosity_check"><PageTransition><ViscosityCheck /></PageTransition></FeatureRoute>} />
      <Route path="/tools/flow-rate" element={<FeatureRoute feature="flow_rate"><PageTransition><FlowRate /></PageTransition></FeatureRoute>} />
      <Route path="/conventional" element={<FeatureRoute feature="drilling_conventional"><PageTransition><Conventional /></PageTransition></FeatureRoute>} />
      <Route path="/chipping" element={<FeatureRoute feature="drilling_chipping"><PageTransition><ToolComingSoon title="Chipping" /></PageTransition></FeatureRoute>} />
      <Route path="/tools/calculators" element={<FeatureRoute feature="calculators"><PageTransition><Calculators /></PageTransition></FeatureRoute>} />
      <Route path="/calculators" element={<Navigate to="/tools/calculators" replace />} />
      <Route path="/document-builder" element={<FeatureRoute feature="document_builder"><PageTransition><DocumentBuilder /></PageTransition></FeatureRoute>} />
      <Route path="/admin/feature-permissions" element={<FeatureRoute feature="feature_permissions"><PageTransition><FeaturePermissions /></PageTransition></FeatureRoute>} />
      <Route path="/documents" element={<FeatureRoute feature="documents"><PageTransition><Documents /></PageTransition></FeatureRoute>} />
      <Route path="/method-run-sheet/:sheetId/:method" element={<PageTransition><MethodRunSheet /></PageTransition>} />
      <Route path="/tools/coming-soon-support" element={<FeatureRoute feature="support"><PageTransition><ToolComingSoon title="Support" /></PageTransition></FeatureRoute>} />
    <Route path="/handover" element={<FeatureRoute feature="handover"><PageTransition><Handover /></PageTransition></FeatureRoute>} />
      <Route path="/handover/new" element={<FeatureRoute feature="handover"><PageTransition><Handover formId="new" /></PageTransition></FeatureRoute>} />
      <Route path="/handover/:formId" element={<FeatureRoute feature="handover"><HandoverRoute /></FeatureRoute>} />
    <Route path="/reporting" element={<FeatureRoute feature="reporting"><PageTransition><Reporting /></PageTransition></FeatureRoute>} />
    </Route>
    <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
    <Route path="/client-portal/login" element={<PageTransition><ClientPortalLogin /></PageTransition>} />
    <Route path="/legal" element={<PageTransition><Legal /></PageTransition>} />
    {WEB_BASE_PATH === "/" && (
      <Route path="/pages/drill-log-pro/client-portal/login" element={<PageTransition><ClientPortalLogin /></PageTransition>} />
    )}
    <Route path="/register" element={<PageTransition><Register /></PageTransition>} />
    <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />
    <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
    <Route path="/auth/callback" element={<PageTransition><AuthCallback /></PageTransition>} />
    <Route path="*" element={<PageTransition><PageNotFound /></PageTransition>} />
    </Routes>
    </AnimatePresence>
    </Suspense>
    <MobileTabBar />
    </>
  );
};


function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <FeaturePermissionContextProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router basename={import.meta.env.VITE_APP_BASE_PATH || "/"}>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
      </FeaturePermissionContextProvider>
    </AuthProvider>
    </ThemeProvider>
  )
}

export default App